/**
 * Chat Log Service
 * Handles logging of user prompts and AI responses
 */

export interface ChatLog {
  id: string;
  timestamp: string;
  userPrompt: string;
  aiResponse: string;
  dashboardName?: string;
  toonData?: string;
  conversationId?: string;
  firmId?: number;
  tokenUsage?: {
    promptTokens?: number;
    completionTokens?: number;
    totalTokens?: number;
  };
  tokenInfo?: {
    tokenPresent?: boolean;
    tokenLength?: number;
    maskedToken?: string;
  };
}

const STORAGE_KEY = "pharmasoft_chat_logs";
const MAX_LOGS = 1000; // Maximum number of logs to keep

/**
 * Get all chat logs from localStorage
 */
export function getChatLogs(): ChatLog[] {
  try {
    const logsJson = localStorage.getItem(STORAGE_KEY);
    if (!logsJson) return [];
    return JSON.parse(logsJson);
  } catch (error) {
    console.error("Error reading chat logs:", error);
    return [];
  }
}

/**
 * Save a chat log
 */
export function saveChatLog(log: Omit<ChatLog, "id" | "timestamp">): void {
  try {
    const logs = getChatLogs();
    const newLog: ChatLog = {
      ...log,
      id: `log-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
    };

    // Add new log at the beginning
    logs.unshift(newLog);

    // Keep only the most recent logs
    if (logs.length > MAX_LOGS) {
      logs.splice(MAX_LOGS);
    }

    // Save to localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(logs));

    // Also log to console for debugging
    console.log("📝 Chat Log Saved:", {
      timestamp: newLog.timestamp,
      userPrompt: newLog.userPrompt,
      aiResponse: newLog.aiResponse.substring(0, 100) + (newLog.aiResponse.length > 100 ? "..." : ""),
      dashboardName: newLog.dashboardName,
      tokenUsage: newLog.tokenUsage,
      tokenInfo: newLog.tokenInfo,
    });
  } catch (error) {
    console.error("Error saving chat log:", error);
  }
}

/**
 * Log user prompt
 */
export function logUserPrompt(
  prompt: string,
  conversationId?: string,
  firmId?: number,
  tokenInfo?: {
    tokenPresent?: boolean;
    tokenLength?: number;
    maskedToken?: string;
  }
): void {
  console.log("👤 User Prompt:", {
    prompt,
    timestamp: new Date().toISOString(),
    conversationId,
    firmId,
    tokenInfo,
  });
}

/**
 * Log AI response
 */
export function logAIResponse(
  userPrompt: string,
  aiResponse: string,
  options?: {
    dashboardName?: string;
    toonData?: string;
    conversationId?: string;
    firmId?: number;
    tokenUsage?: {
      promptTokens?: number;
      completionTokens?: number;
      totalTokens?: number;
    };
    tokenInfo?: {
      tokenPresent?: boolean;
      tokenLength?: number;
      maskedToken?: string;
    };
  }
): void {
  // Log to console
  console.log("🤖 AI Response:", {
    userPrompt,
    aiResponse: aiResponse.substring(0, 200) + (aiResponse.length > 200 ? "..." : ""),
    timestamp: new Date().toISOString(),
    tokenUsage: options?.tokenUsage,
    tokenInfo: options?.tokenInfo,
    ...options,
  });

  // Save to persistent storage
  saveChatLog({
    userPrompt,
    aiResponse,
    dashboardName: options?.dashboardName,
    toonData: options?.toonData,
    conversationId: options?.conversationId,
    firmId: options?.firmId,
    tokenUsage: options?.tokenUsage,
    tokenInfo: options?.tokenInfo,
  });
}

/**
 * Clear all chat logs
 */
export function clearChatLogs(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    console.log("🗑️ Chat logs cleared");
  } catch (error) {
    console.error("Error clearing chat logs:", error);
  }
}

/**
 * Export chat logs as JSON
 */
export function exportChatLogs(): string {
  const logs = getChatLogs();
  return JSON.stringify(logs, null, 2);
}
